/**
 * EditorForm -- 중앙 5fr 편집 폼 영역
 *
 * EditDrawer의 폼 렌더링 로직을 재사용하되,
 * 닫기 버튼 없이 항상 표시되는 형태.
 */

import { useMemo, useState, useCallback } from 'react'
import {
  MessageSquare, Users, Calendar, Image, Phone,
  CreditCard, UserCheck, BookOpen, Heart, Clock, Info,
  Type, Music, Share2, Settings,
} from 'lucide-react'
import { Toggle, Select, Input } from '../ui/index.js'
import { cn } from '../../lib/cn.js'
import { useInvitationStore } from '../../stores/invitationStore.js'
import { type SectionId } from '@cugumi/shared/schemas'
import {
  type AllSectionId,
  getSectionLabel,
  getSectionComponent,
  TOGGLEABLE_SECTIONS,
  SECTION_COMPONENTS,
} from './types.js'
import type { FlatInvitation } from '../../stores/invitationStore.js'
import styles from './EditorForm.module.css'

const SECTION_SUBTITLE_MAP: Partial<Record<AllSectionId, string>> = {
  ceremony: '예식 날짜와 장소를 입력해주세요',
  parents: '혼주 정보를 입력해주세요',
}

const SECTION_ICON_MAP: Partial<Record<AllSectionId, { icon: React.ReactNode; className: string }>> = {
  greeting: { icon: <MessageSquare size={14} />, className: styles.iconGreeting },
  parents: { icon: <Users size={14} />, className: styles.iconParents },
  ceremony: { icon: <Calendar size={14} />, className: styles.iconCeremony },
  gallery: { icon: <Image size={14} />, className: styles.iconGallery },
  contacts: { icon: <Phone size={14} />, className: styles.iconContacts },
  accounts: { icon: <CreditCard size={14} />, className: styles.iconAccounts },
  rsvp: { icon: <UserCheck size={14} />, className: styles.iconRsvp },
  guestbook: { icon: <BookOpen size={14} />, className: styles.iconGuestbook },
  timeline: { icon: <Heart size={14} />, className: styles.iconTimeline },
  schedule: { icon: <Clock size={14} />, className: styles.iconSchedule },
  notices: { icon: <Info size={14} />, className: styles.iconNotices },
  fontStyle: { icon: <Type size={14} />, className: styles.iconFontStyle },
  bgm: { icon: <Music size={14} />, className: styles.iconBgm },
  kakaoShare: { icon: <Share2 size={14} />, className: styles.iconKakaoShare },
  settings: { icon: <Settings size={14} />, className: styles.iconSettings },
}

interface EditorFormProps {
  invitation: FlatInvitation
  activeSection: AllSectionId
}

export function EditorForm({ invitation, activeSection }: EditorFormProps) {
  const updateNested = useInvitationStore((s) => s.updateNested)
  const enabledSections = invitation.enabledSections ?? {}

  const isContentSection = activeSection in SECTION_COMPONENTS
  const label = getSectionLabel(activeSection)
  const iconInfo = SECTION_ICON_MAP[activeSection]

  const toggleKey = isContentSection
    ? TOGGLEABLE_SECTIONS[activeSection as SectionId] ?? null
    : null

  const isEnabled = useMemo(() => {
    if (!toggleKey) return true
    return (enabledSections as Record<string, boolean>)[toggleKey] !== false
  }, [toggleKey, enabledSections])

  function handleToggle() {
    if (!toggleKey) return
    updateNested(`enabledSections.${toggleKey}`, !isEnabled)
  }

  const subtitle = SECTION_SUBTITLE_MAP[activeSection]

  // 섹션 여백 조절
  const sectionSpacing = (invitation.settings as Record<string, unknown>)?.sectionSpacing as Record<string, string> | undefined
  const sectionId = activeSection as string
  const rawSpacing = sectionSpacing?.[sectionId] ?? 'default'
  const isPreset = ['default', 'lg', 'xl'].includes(rawSpacing)
  const spacingSelectValue = isPreset ? rawSpacing : 'custom'
  const [customPx, setCustomPx] = useState(() => isPreset ? '' : rawSpacing)

  const handleSpacingChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value
    if (val === 'custom') {
      const px = '40'
      setCustomPx(px)
      updateNested(`settings.sectionSpacing.${sectionId}`, px)
    } else {
      updateNested(`settings.sectionSpacing.${sectionId}`, val)
    }
  }, [sectionId, updateNested])

  const handleCustomPxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Math.min(Math.max(0, Number(e.target.value) || 0), 200).toString()
    setCustomPx(v)
    updateNested(`settings.sectionSpacing.${sectionId}`, v)
  }, [sectionId, updateNested])

  const SectionComponent = getSectionComponent(activeSection)
  if (!SectionComponent) return null

  const SPACING_OPTIONS = [
    { value: 'default', label: '기본' },
    { value: 'lg', label: '여유있게' },
    { value: 'xl', label: '넓게' },
    { value: 'custom', label: '직접입력' },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          {iconInfo && (
            <span className={cn(styles.icon, iconInfo.className)}>
              {iconInfo.icon}
            </span>
          )}
          <div className={styles.headerText}>
            <span className={styles.label}>{label}</span>
            {subtitle && (
              <span className={styles.subtitle}>{subtitle}</span>
            )}
          </div>
        </div>
        <div className={styles.headerRight}>
          {isContentSection && (
            <div className={styles.spacingControl}>
              <span className={styles.spacingLabel}>여백</span>
              <Select
                size="sm"
                value={spacingSelectValue}
                onChange={handleSpacingChange}
                options={SPACING_OPTIONS}
              />
              {spacingSelectValue === 'custom' && (
                <div className={styles.spacingCustomInput}>
                  <Input
                    type="number"
                    size="sm"
                    value={customPx}
                    onChange={handleCustomPxChange}
                    min={0}
                    max={200}
                  />
                  <span className={styles.spacingSuffix}>px</span>
                </div>
              )}
            </div>
          )}
          {toggleKey && (
            <Toggle
              checked={isEnabled}
              onChange={handleToggle}
              size="sm"
            />
          )}
        </div>
      </div>

      <div className={styles.body}>
        <SectionComponent />
      </div>
    </div>
  )
}
